import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Info, Users, Megaphone, FileText, MessageSquare, Newspaper, Image as ImageIcon, Video, Clock, ChevronDown, Calculator } from 'lucide-react';

// Industry benchmarks for creation hours
const CREATION_BENCHMARKS = {
  blog: {
    conventional: 6,
    ai: 1.5
  },
  social: {
    conventional: 1,
    ai: 0.2
  },
  pr: {
    conventional: 8,
    ai: 2
  },
  visual: {
    conventional: 3,
    ai: 0.5
  },
  video: {
    conventional: 8,
    ai: 1
  } // per minute
};
const CAMPAIGN_MULTIPLIERS = {
  blogArticles: 3,
  socialPosts: 15,
  pressReleases: 1,
  visuals: 10,
  videoMinutes: 2
};
const ANNUAL_COST_PER_PERSON = 80000;
const WORK_HOURS_PER_YEAR = 2080;
const HOURLY_RATE = ANNUAL_COST_PER_PERSON / WORK_HOURS_PER_YEAR;

// gimmefy credit costs
const CREDIT_COST = 0.10; // $0.10 per credit
const MONTHLY_SETUP_FEE = 250; // $250/month (one-time $3,000 setup fee amortized over 12 months)

// Credit usage per asset based on typical workflow
const CREDIT_USAGE = {
  // Pre-creation workflow credits (applied to each asset)
  research: 80,
  // 20 credits × 4 passes
  planning: 4,
  // 1 credit × 4 passes
  ideation: 50,
  // 10 credits × 5 passes

  // Creation credits (asset-specific)
  blog: 50,
  // 10 credits × 5 passes
  social: 40,
  // 10 credits × 4 passes
  pr: 80,
  // 20 credits × 4 passes
  visual: 40,
  // 10 credits × 4 passes
  video: 1800 // per minute - 600 credits × 3 passes
};
export function EnterpriseROICalculator() {
  const [contentVolume, setContentVolume] = useState({
    blogArticles: 0,
    socialPosts: 0,
    pressReleases: 0,
    visuals: 0,
    videoMinutes: 0
  });
  const [campaigns, setCampaigns] = useState<number>(0);
  const [teamConfig, setTeamConfig] = useState({
    staffCount: 0,
    annualCostPerPerson: ANNUAL_COST_PER_PERSON
  });
  const [workflowHours, setWorkflowHours] = useState({
    research: 1.5,
    planning: 1.0,
    ideation: 0.75
  });
  const [_showCustomCreation, _setShowCustomCreation] = useState(false);
  const [activePreset, setActivePreset] = useState<string | null>("In-house team");
  const [isContentOpen, setIsContentOpen] = useState(false);
  useEffect(() => {
    if (campaigns > 0) {
      setContentVolume({
        blogArticles: campaigns * CAMPAIGN_MULTIPLIERS.blogArticles,
        socialPosts: campaigns * CAMPAIGN_MULTIPLIERS.socialPosts,
        pressReleases: campaigns * CAMPAIGN_MULTIPLIERS.pressReleases,
        visuals: campaigns * CAMPAIGN_MULTIPLIERS.visuals,
        videoMinutes: campaigns * CAMPAIGN_MULTIPLIERS.videoMinutes
      });
    }
  }, [campaigns]);

  // Load default preset on initial mount
  useEffect(() => {
    loadPreset(presets[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const calculations = useMemo(() => {
    const {
      research,
      planning,
      ideation
    } = workflowHours;
    const preCreationHours = research + planning + ideation;

    // Calculate hourly rate based on team configuration
    const hourlyRate = teamConfig.annualCostPerPerson / WORK_HOURS_PER_YEAR;

    // Calculate conventional hours
    const conventionalBlog = contentVolume.blogArticles * (preCreationHours + CREATION_BENCHMARKS.blog.conventional);
    const conventionalSocial = contentVolume.socialPosts * (preCreationHours + CREATION_BENCHMARKS.social.conventional);
    const conventionalPR = contentVolume.pressReleases * (preCreationHours + CREATION_BENCHMARKS.pr.conventional);
    const conventionalVisual = contentVolume.visuals * (preCreationHours + CREATION_BENCHMARKS.visual.conventional);
    const conventionalVideo = contentVolume.videoMinutes * (preCreationHours + CREATION_BENCHMARKS.video.conventional);
    const totalConventionalHours = conventionalBlog + conventionalSocial + conventionalPR + conventionalVisual + conventionalVideo;

    // Calculate AI hours
    const aiBlog = contentVolume.blogArticles * (preCreationHours + CREATION_BENCHMARKS.blog.ai);
    const aiSocial = contentVolume.socialPosts * (preCreationHours + CREATION_BENCHMARKS.social.ai);
    const aiPR = contentVolume.pressReleases * (preCreationHours + CREATION_BENCHMARKS.pr.ai);
    const aiVisual = contentVolume.visuals * (preCreationHours + CREATION_BENCHMARKS.visual.ai);
    const aiVideo = contentVolume.videoMinutes * (preCreationHours + CREATION_BENCHMARKS.video.ai);
    const totalAIHours = aiBlog + aiSocial + aiPR + aiVisual + aiVideo;

    // Calculate Gimmefy credit costs
    const blogCredits = contentVolume.blogArticles * (CREDIT_USAGE.research + CREDIT_USAGE.planning + CREDIT_USAGE.ideation + CREDIT_USAGE.blog);
    const socialCredits = contentVolume.socialPosts * (CREDIT_USAGE.research + CREDIT_USAGE.planning + CREDIT_USAGE.ideation + CREDIT_USAGE.social);
    const prCredits = contentVolume.pressReleases * (CREDIT_USAGE.research + CREDIT_USAGE.planning + CREDIT_USAGE.ideation + CREDIT_USAGE.pr);
    const visualCredits = contentVolume.visuals * (CREDIT_USAGE.research + CREDIT_USAGE.planning + CREDIT_USAGE.ideation + CREDIT_USAGE.visual);
    const videoCredits = contentVolume.videoMinutes * (CREDIT_USAGE.research + CREDIT_USAGE.planning + CREDIT_USAGE.ideation + CREDIT_USAGE.video);
    const totalCredits = blogCredits + socialCredits + prCredits + visualCredits + videoCredits;
    const monthlyGimmefyCost = totalCredits * CREDIT_COST + MONTHLY_SETUP_FEE;
    const annualGimmefyCost = monthlyGimmefyCost * 12;

    // Calculate costs using hourly rate (time savings are total, not per person)
    const conventionalCost = totalConventionalHours * hourlyRate;
    const aiCost = totalAIHours * hourlyRate;
    const monthlySavingsHours = totalConventionalHours - totalAIHours;
    const monthlySavingsCost = (totalConventionalHours - totalAIHours) * hourlyRate;

    // Quality improvement: 10% of total team salary
    const totalTeamSalary = teamConfig.staffCount * teamConfig.annualCostPerPerson;
    const qualityImprovement = totalTeamSalary * 0.10;
    const monthlyQualityImprovement = qualityImprovement / 12;

    // Net monthly savings after Gimmefy costs
    const netMonthlySavings = monthlySavingsCost - monthlyGimmefyCost;
    const totalMonthlySavings = netMonthlySavings + monthlyQualityImprovement;

    // Annual savings after Gimmefy costs
    const annualSavingsCost = netMonthlySavings * 12 + qualityImprovement;
    return {
      totalConventionalHours: Math.round(totalConventionalHours),
      totalAIHours: Math.round(totalAIHours),
      conventionalCost,
      aiCost,
      monthlySavingsHours: Math.round(monthlySavingsHours),
      monthlySavingsCost,
      monthlyGimmefyCost,
      annualGimmefyCost,
      totalCredits: Math.round(totalCredits),
      netMonthlySavings,
      totalMonthlySavings,
      qualityImprovement,
      monthlyQualityImprovement,
      annualSavingsCost,
      roiUplift: 20 // Fixed industry benchmark
    };
  }, [contentVolume, workflowHours, teamConfig]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };
  const InfoTooltip = ({
    content
  }: {
    content: string | React.ReactNode;
  }) => <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 flex-shrink-0 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent>
          {typeof content === 'string' ? <p className="max-w-xs">{content}</p> : content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>;
  const presets = [{
    name: "In-house team",
    data: {
      staffCount: 5,
      campaigns: 2,
      contentVolume: {
        blogArticles: 12,
        socialPosts: 60,
        pressReleases: 4,
        visuals: 40,
        videoMinutes: 8
      }
    }
  }, {
    name: "Mid-sized brands/teams",
    data: {
      staffCount: 10,
      campaigns: 5,
      contentVolume: {
        blogArticles: 30,
        socialPosts: 150,
        pressReleases: 10,
        visuals: 100,
        videoMinutes: 20
      }
    }
  }, {
    name: "Large brands/agencies",
    data: {
      staffCount: 25,
      campaigns: 12,
      contentVolume: {
        blogArticles: 72,
        socialPosts: 360,
        pressReleases: 24,
        visuals: 240,
        videoMinutes: 48
      }
    }
  }];
  const loadPreset = (preset: typeof presets[0]) => {
    setTeamConfig({
      ...teamConfig,
      staffCount: preset.data.staffCount
    });
    setCampaigns(preset.data.campaigns);
    setContentVolume(preset.data.contentVolume);
    setActivePreset(preset.name);
    setIsContentOpen(false);
  };
  const handleManualEdit = () => {
    setActivePreset(null);
  };
  return <div className="container mx-auto px-4 py-4 md:py-6">
      <motion.div 
        className="text-center mb-8 md:mb-10"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.15 } }
        }}
      >
        <motion.div
          variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
          className="mb-4"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-medium">
            <Calculator className="h-4 w-4" />
            ROI Calculator
          </span>
        </motion.div>
        <motion.h2 
          variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight"
        >
          See Your{" "}
          <span className="bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-600 bg-clip-text text-transparent">
            Real Savings
          </span>
          <br className="hidden sm:block" />
          <span className="text-foreground">End-to-End Content ROI</span>
        </motion.h2>
        <motion.p 
          variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
          className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
        >
          Enter your team's workflow details and asset volumes to reveal tailored cost and efficiency gains at every stage of the content lifecycle.
        </motion.p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
        {/* Left Column - Input Fields */}
        <div className="space-y-6">
          {/* Preset Buttons */}
          <div className="mb-6 flex flex-wrap gap-3 justify-start">
            {presets.map(preset => <Button key={preset.name} variant={activePreset === preset.name ? "hero" : "outline"} onClick={() => loadPreset(preset)} className={`transition-all duration-300 ${activePreset === preset.name ? "shadow-primary scale-105 border-0" : "hover:border-primary/50 hover:bg-primary/10 hover:shadow-md hover:text-foreground"}`}>
                {preset.name}
              </Button>)}
          </div>
          <Card className="hover:shadow-lg transition-all duration-300 border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600">
            <CardHeader className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                  <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                Start with Your Team Details
              </CardTitle>
              <CardDescription className="text-base">How Much Content Does Your Team Deliver Every Month?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="group border-l-4 border-gray-200 dark:border-gray-700 pl-4 hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                  <Label htmlFor="teamSize" className="flex items-center gap-1.5 mb-3 font-semibold">
                    <div className="p-1.5 rounded-md bg-gray-100 dark:bg-gray-800">
                      <Users className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    Team size
                  </Label>
                  <Select value={teamConfig.staffCount > 0 ? teamConfig.staffCount.toString() : "0"} onValueChange={value => {
                  setTeamConfig({
                    ...teamConfig,
                    staffCount: parseInt(value)
                  });
                  handleManualEdit();
                }}>
                    <SelectTrigger className="h-12 text-base focus-visible:ring-primary focus-visible:ring-2 focus-visible:border-primary focus-visible:bg-primary/5 transition-all duration-200">
                      <SelectValue>
                        {teamConfig.staffCount > 0 ? teamConfig.staffCount : "Select team size"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0</SelectItem>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="30">30</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                      <SelectItem value="50">50+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="group border-l-4 border-gray-200 dark:border-gray-700 pl-4 hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                  <Label htmlFor="campaigns" className="flex items-center gap-1.5 mb-3 font-semibold">
                    <div className="p-1.5 rounded-md bg-gray-100 dark:bg-gray-800">
                      <Megaphone className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    Number of campaigns
                    <InfoTooltip content={<div>
                        <div className="font-semibold mb-2">Median Content Volume per Campaign</div>
                        <div className="space-y-1 text-xs">
                          <div>Blog Articles: 6</div>
                          <div>Social Posts: 30</div>
                          <div>Press Releases: 2</div>
                          <div>Visual Assets: 20</div>
                          <div>Video Minutes: 4</div>
                        </div>
                      </div>} />
                  </Label>
                  <Select value={campaigns > 0 ? campaigns.toString() : "0"} onValueChange={value => {
                  setCampaigns(parseInt(value));
                  handleManualEdit();
                }}>
                    <SelectTrigger className="h-12 text-base focus-visible:ring-accent focus-visible:ring-2 focus-visible:border-accent focus-visible:bg-accent/5 transition-all duration-200">
                      <SelectValue>
                        {campaigns > 0 ? campaigns : "Select campaigns"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0</SelectItem>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="8">8</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Collapsible open={isContentOpen} onOpenChange={setIsContentOpen} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Customize Content Volume</p>
                    {!isContentOpen && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {contentVolume.blogArticles} long-form articles • {contentVolume.socialPosts} social posts • {contentVolume.pressReleases} press releases • {contentVolume.visuals} visuals • {contentVolume.videoMinutes} video minutes
                      </p>
                    )}
                  </div>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-9 p-0">
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isContentOpen ? 'rotate-180' : ''}`} />
                      <span className="sr-only">Toggle content volume</span>
                    </Button>
                  </CollapsibleTrigger>
                </div>
                
                <CollapsibleContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="group">
                      <Label htmlFor="blogArticles" className="flex items-center gap-1.5 mb-3">
                        <FileText className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        Long-form articles
                        <InfoTooltip content="Long-form articles, each a unique content unit" />
                      </Label>
                      <Input id="blogArticles" type="number" min="0" value={contentVolume.blogArticles || ''} onChange={e => {
                      setContentVolume({
                        ...contentVolume,
                        blogArticles: parseInt(e.target.value) || 0
                      });
                      handleManualEdit();
                    }} placeholder="0" className="h-12 text-base focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                    </div>
                    <div className="group">
                      <Label htmlFor="socialPosts" className="flex items-center gap-1.5 mb-3">
                        <MessageSquare className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        Social posts
                        <InfoTooltip content="Posts published on brand channels (organic/paid), each with its own copy/visual/video" />
                      </Label>
                      <Input id="socialPosts" type="number" min="0" value={contentVolume.socialPosts || ''} onChange={e => {
                      setContentVolume({
                        ...contentVolume,
                        socialPosts: parseInt(e.target.value) || 0
                      });
                      handleManualEdit();
                    }} placeholder="0" className="h-12 text-base focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                    </div>
                    <div className="group">
                      <Label htmlFor="pressReleases" className="flex items-center gap-1.5 mb-3">
                        <Newspaper className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        Press releases
                        <InfoTooltip content="Unique news/PR communications" />
                      </Label>
                      <Input id="pressReleases" type="number" min="0" value={contentVolume.pressReleases || ''} onChange={e => {
                      setContentVolume({
                        ...contentVolume,
                        pressReleases: parseInt(e.target.value) || 0
                      });
                      handleManualEdit();
                    }} placeholder="0" className="h-12 text-base focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                    </div>
                    <div className="group">
                      <Label htmlFor="visuals" className="flex items-center gap-1.5 mb-3">
                        <ImageIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        Visual assets
                        <InfoTooltip content="Custom graphics/ad designs (hero images, campaign banners, infographics, ad sets, etc.)" />
                      </Label>
                      <Input id="visuals" type="number" min="0" value={contentVolume.visuals || ''} onChange={e => {
                      setContentVolume({
                        ...contentVolume,
                        visuals: parseInt(e.target.value) || 0
                      });
                      handleManualEdit();
                    }} placeholder="0" className="h-12 text-base focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                    </div>
                    <div className="group">
                      <Label htmlFor="videoMinutes" className="flex items-center gap-1.5 mb-3">
                        <Video className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        Video minutes
                        <InfoTooltip content="Produced minutes of video content (all formats)" />
                      </Label>
                      <Input id="videoMinutes" type="number" min="0" value={contentVolume.videoMinutes || ''} onChange={e => {
                      setContentVolume({
                        ...contentVolume,
                        videoMinutes: parseInt(e.target.value) || 0
                      });
                      handleManualEdit();
                    }} placeholder="0" className="h-12 text-base focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300 border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600">
            <CardHeader className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                  <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                Time Invested per Content
              </CardTitle>
              <CardDescription className="text-base">Either use the pre-filled industry averages or adjust to reflect your team's real workflow.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="group">
                  <Label htmlFor="research" className="flex items-center gap-1.5 mb-3">
                    <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    Research
                    <InfoTooltip content="Industry average: 1.5 hours per asset" />
                  </Label>
                  <Input id="research" type="number" min="0" step="0.25" value={workflowHours.research} onChange={e => setWorkflowHours({
                  ...workflowHours,
                  research: parseFloat(e.target.value) || 0
                })} className="focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                </div>
                <div className="group">
                  <Label htmlFor="planning" className="flex items-center gap-1.5 mb-3">
                    <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    Planning
                    <InfoTooltip content="Industry average: 1 hour per asset" />
                  </Label>
                  <Input id="planning" type="number" min="0" step="0.25" value={workflowHours.planning} onChange={e => setWorkflowHours({
                  ...workflowHours,
                  planning: parseFloat(e.target.value) || 0
                })} className="focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                </div>
                <div className="group">
                  <Label htmlFor="ideation" className="flex items-center gap-1.5 mb-3">
                    <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    Ideation
                    <InfoTooltip content="Industry average: 0.75 hours per asset" />
                  </Label>
                  <Input id="ideation" type="number" min="0" step="0.25" value={workflowHours.ideation} onChange={e => setWorkflowHours({
                  ...workflowHours,
                  ideation: parseFloat(e.target.value) || 0
                })} className="focus-visible:ring-primary/50 focus-visible:scale-[1.02] transition-all duration-200" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Results Display */}
        <Card className="bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 lg:h-fit lg:sticky lg:top-8 border-gray-200 dark:border-gray-800 shadow-lg">
          <CardHeader className="bg-gray-100 dark:bg-gray-800 border-b-2 border-gray-200 dark:border-gray-700 p-4 md:p-6">
            <CardTitle className="text-xl md:text-2xl font-bold">Your Custom Savings Snapshot</CardTitle>
            <CardDescription className="text-sm md:text-base">Here's what your team could achieve—calculated using your inputs and trusted industry figures.</CardDescription>
          </CardHeader>
          <CardContent className="p-3 md:p-4 lg:p-6">
            {/* Mobile Card-Based View */}
            <div className="space-y-4 lg:hidden">
              <div className="border rounded-lg p-4 bg-muted/20">
                <h4 className="font-bold mb-3 text-sm">Total Team Hours</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Conventional:</span>
                    <span className="font-medium text-sm">{formatNumber(calculations.totalConventionalHours)} hrs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">With AI:</span>
                    <span className="font-medium text-sm">{formatNumber(calculations.totalAIHours)} hrs</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400">Savings:</span>
                    <span className="font-bold text-sm text-green-600 dark:text-green-400">{formatNumber(calculations.monthlySavingsHours)} hrs</span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-bold mb-3 text-sm">Productivity Savings (25% avg.)</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Conventional:</span>
                    <span className="font-medium text-sm">{formatCurrency(calculations.conventionalCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">With AI:</span>
                    <span className="font-medium text-sm">{formatCurrency(calculations.aiCost)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400">Savings:</span>
                    <span className="font-bold text-sm text-green-600 dark:text-green-400">{formatCurrency(calculations.monthlySavingsCost)}</span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-muted/20">
                <h4 className="font-bold mb-3 text-sm flex items-center gap-1">
                  gimmefy Cost
                  <InfoTooltip content={`Based on ${formatNumber(calculations.totalCredits)} credits at $${CREDIT_COST} per credit plus $${MONTHLY_SETUP_FEE}/month setup fee (one-time $3,000 setup fee amortized over typical 12-month contract)`} />
                </h4>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Monthly cost:</span>
                  <span className="font-semibold text-sm text-red-600 dark:text-red-400">
                    {formatCurrency(calculations.monthlyGimmefyCost)}
                  </span>
                </div>
              </div>

              <div className="border-2 rounded-lg p-4 bg-gradient-to-r from-green-100 via-green-50 to-green-100 dark:from-green-950/40 dark:via-green-900/30 dark:to-green-950/40 border-green-300 dark:border-green-700">
                <h4 className="font-bold mb-3 text-sm">Net monthly savings</h4>
                <div className="text-center">
                  <span className="font-bold text-xl text-green-600 dark:text-green-400">
                    {formatCurrency(calculations.netMonthlySavings)}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
                    <th className="text-left py-4 px-4 font-bold">Metric</th>
                    <th className="text-right py-4 px-4 font-bold">Conventional</th>
                    <th className="text-right py-4 px-4 font-bold">With gimmefy AI</th>
                    <th className="text-right py-4 px-4 font-bold text-green-600 dark:text-green-400">Monthly Savings</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors bg-muted/20">
                    <td className="py-3 px-4 font-medium">Total Team Hours</td>
                    <td className="text-right py-3 px-4">
                      <motion.span key={calculations.totalConventionalHours} initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.3
                    }}>
                        {formatNumber(calculations.totalConventionalHours)} hrs
                      </motion.span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <motion.span key={calculations.totalAIHours} initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.3
                    }}>
                        {formatNumber(calculations.totalAIHours)} hrs
                      </motion.span>
                    </td>
                    <td className="text-right py-3 px-4 text-green-600 dark:text-green-400 font-semibold">
                      <motion.span key={calculations.monthlySavingsHours} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      duration: 0.3
                    }}>
                        {formatNumber(calculations.monthlySavingsHours)} hrs
                      </motion.span>
                    </td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                    <td className="py-3 px-4 font-medium">Productivity Savings (25% avg.)</td>
                    <td className="text-right py-3 px-4">
                      <motion.span key={calculations.conventionalCost} initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.3
                    }}>
                        {formatCurrency(calculations.conventionalCost)}
                      </motion.span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <motion.span key={calculations.aiCost} initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.3
                    }}>
                        {formatCurrency(calculations.aiCost)}
                      </motion.span>
                    </td>
                    <td className="text-right py-3 px-4 text-green-600 dark:text-green-400 font-semibold">
                      <motion.span key={calculations.monthlySavingsCost} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      duration: 0.3
                    }}>
                        {formatCurrency(calculations.monthlySavingsCost)}
                      </motion.span>
                    </td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors bg-muted/20">
                    <td className="py-3 px-4 font-medium">
                      gimmefy Cost
                      <InfoTooltip content={`Based on ${formatNumber(calculations.totalCredits)} credits at $${CREDIT_COST} per credit plus $${MONTHLY_SETUP_FEE}/month setup fee (one-time $3,000 setup fee amortized over typical 12-month contract)`} />
                    </td>
                    <td className="text-right py-3 px-4">—</td>
                    <td className="text-right py-3 px-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400 font-semibold">
                        <motion.span key={calculations.monthlyGimmefyCost} initial={{
                          opacity: 0,
                          y: -10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={{
                          duration: 0.3
                        }}>
                          {formatCurrency(calculations.monthlyGimmefyCost)}
                        </motion.span>
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">—</td>
                  </tr>
                  <tr className="bg-gradient-to-r from-green-100 via-green-50 to-green-100 dark:from-green-950/40 dark:via-green-900/30 dark:to-green-950/40 border-2 border-green-300 dark:border-green-700 shadow-md">
                    <td className="py-5 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg">Net monthly savings</span>
                      </div>
                    </td>
                    <td className="text-right py-5 px-4">—</td>
                    <td className="text-right py-5 px-4">—</td>
                    <td className="text-right py-5 px-4">
                      <motion.span 
                        key={calculations.netMonthlySavings} 
                        initial={{ opacity: 0, scale: 0.8 }} 
                        animate={{ opacity: 1, scale: 1 }} 
                        transition={{ duration: 0.5, type: "spring" }}
                        className="text-lg font-bold text-green-600 dark:text-green-400"
                      >
                        {formatCurrency(calculations.netMonthlySavings)}
                      </motion.span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Additional Benefits Cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-blue-100 via-blue-50 to-cyan-100 dark:from-blue-950/40 dark:via-blue-900/20 dark:to-cyan-950/40 border-2 border-blue-300 dark:border-blue-700 shadow-accent hover:shadow-lg transition-shadow">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold">
                      BONUS VALUE
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">Quality Improvement</div>
                  <div className="text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                    <motion.span key={calculations.monthlyQualityImprovement} initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    duration: 0.3
                  }}>
                      {formatCurrency(calculations.monthlyQualityImprovement)}
                    </motion.span>
                  </div>
                  <p className="text-sm text-blue-900/80 dark:text-blue-200/80">
                    Additional value from 10% improvement in content quality and performance
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 via-white to-purple-50 dark:from-purple-950/20 dark:via-gray-950 dark:to-purple-950/20 border-2 border-purple-200 dark:border-purple-900 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-purple-600 dark:bg-purple-500 text-white text-xs font-bold">
                      PERFORMANCE
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-purple-900 dark:text-purple-200 mb-2">ROI Uplift</div>
                  <div className="text-3xl md:text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                    +{calculations.roiUplift}%
                  </div>
                  <p className="text-sm text-purple-900/80 dark:text-purple-200/80">
                    Average performance improvement from AI-enhanced workflows
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-muted-foreground">
                *Based on 2025 content marketing studies. AI-generated content is 4.7× cheaper than traditional methods (Ahrefs). 
                All calculations use ${formatNumber(ANNUAL_COST_PER_PERSON)} fully loaded annual cost per marketing professional 
                (${HOURLY_RATE.toFixed(2)}/hour based on {formatNumber(WORK_HOURS_PER_YEAR)} work hours/year).
              </p>
              <div className="mt-4 flex justify-center">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open('https://blog.gimmefy.ai/blog/behind-gimmefys-roi-calculator-for-enterprises', '_blank')}
                  className="hover:bg-primary/10 hover:border-primary"
                >
                  Read more about our methodology
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}